import React, { useState, useEffect, useCallback, useMemo } from "react"
import {
    Box,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    ListSubheader,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import { useTranslation } from "react-i18next"
import { SharedFilterValues } from "./SharedFilters"
import ExportButton from "./ExportButton"
import reportsService from "../../services/reportsService"
import apiClient from "../../services/apiClient"
import { formatCentsWithSymbol } from "../../utils/formatCurrency"

interface TicketZTabProps {
    filters: SharedFilterValues
    companyId: string
}

type SessionOption =
    | {
          kind: "physical"
          id: string
          label: string
          raw: any
      }
    | {
          kind: "external"
          id: string
          label: string
          date: string
      }

const fmtC = (v: number, c: string = "EUR") => formatCentsWithSymbol(v, c)
const fmtDateTime = (value?: string) =>
    value
        ? new Date(value).toLocaleString("fr-FR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          })
        : "—"
const fmtDate = (value?: string) =>
    value
        ? new Date(value).toLocaleDateString("fr-FR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
          })
        : "—"

const EXTERNAL_HISTORY_DAYS = 7
const buildExternalSessions = (outletId?: string): SessionOption[] => {
    const out: SessionOption[] = []
    for (let i = 0; i < EXTERNAL_HISTORY_DAYS; i++) {
        const d = new Date()
        d.setDate(d.getDate() - i)
        const date = d.toISOString().slice(0, 10)
        out.push({
            kind: "external",
            id: `daily-external-${date}${outletId ? `-${outletId}` : ""}`,
            label: `Loca — ${fmtDate(d.toISOString())}`,
            date,
        })
    }
    return out
}

const TicketZTab: React.FC<TicketZTabProps> = ({ filters, companyId }) => {
    const { t } = useTranslation()
    const [physicalSessions, setPhysicalSessions] = useState<any[]>([])
    const [selectedSessionId, setSelectedSessionId] = useState<string>("")
    const [report, setReport] = useState<any>(null)
    const [loading, setLoading] = useState(false)

    // Fetch physical sessions whenever outlet/register changes.
    useEffect(() => {
        if (!companyId) {
            setPhysicalSessions([])
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const params = new URLSearchParams({ limit: "50" })
                if (filters.outletId) params.set("outletId", filters.outletId)
                if (filters.registerId)
                    params.set("registerId", filters.registerId)
                const res = await apiClient.get(
                    `/api/companies/${companyId}/cash-sessions?${params.toString()}`
                )
                if (cancelled) return
                setPhysicalSessions(
                    Array.isArray(res?.sessions) ? res.sessions : []
                )
            } catch (err) {
                console.error("Failed to load cash sessions:", err)
                if (!cancelled) setPhysicalSessions([])
            }
        })()
        return () => {
            cancelled = true
        }
    }, [companyId, filters.outletId, filters.registerId])

    const externalSessions = useMemo(
        () => buildExternalSessions(filters.outletId || undefined),
        [filters.outletId]
    )

    const physicalOptions: SessionOption[] = useMemo(
        () =>
            physicalSessions.map((s: any) => {
                const statusLabel = s.status ? ` (${s.status})` : ""
                const registerName =
                    s.register_name || s.registerName || "Caisse"
                const openedAt = s.opened_at || s.openedAt
                return {
                    kind: "physical",
                    id: s.id,
                    label: `${registerName} — ${fmtDateTime(
                        openedAt
                    )}${statusLabel}`,
                    raw: s,
                }
            }),
        [physicalSessions]
    )

    const allOptions: SessionOption[] = useMemo(
        () => [...physicalOptions, ...externalSessions],
        [physicalOptions, externalSessions]
    )

    // Auto-select first available session when the list changes.
    useEffect(() => {
        if (!selectedSessionId && allOptions.length > 0) {
            setSelectedSessionId(allOptions[0].id)
        } else if (
            selectedSessionId &&
            !allOptions.some((o) => o.id === selectedSessionId)
        ) {
            setSelectedSessionId(allOptions[0]?.id || "")
        }
    }, [allOptions, selectedSessionId])

    const selected = useMemo(
        () => allOptions.find((o) => o.id === selectedSessionId),
        [allOptions, selectedSessionId]
    )

    const loadReport = useCallback(async () => {
        if (!companyId || !selected) {
            setReport(null)
            return
        }
        setLoading(true)
        try {
            if (selected.kind === "external") {
                const data = await reportsService.getDailyExternalZReport(
                    companyId,
                    selected.date,
                    filters.outletId || undefined
                )
                setReport(data)
            } else {
                const data = await reportsService.getZReport(
                    companyId,
                    selected.id
                )
                setReport(data)
            }
        } catch (err) {
            console.error("Failed to load Z report:", err)
            setReport(null)
        } finally {
            setLoading(false)
        }
    }, [companyId, selected, filters.outletId])

    useEffect(() => {
        loadReport()
    }, [loadReport])

    const extraFilters =
        selected?.kind === "external"
            ? {
                  mode: "daily_external",
                  date: selected.date,
              }
            : selected?.kind === "physical"
            ? {
                  cashSessionId: selected.id,
              }
            : {}

    return (
        <Box>
            <Paper
                sx={{
                    p: 2,
                    mb: 2,
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <FormControl size="small" sx={{ minWidth: 360 }}>
                    <InputLabel>
                        {t(
                            "exportsComponents.ticketZTab.sessionSelector.label"
                        )}
                    </InputLabel>
                    <Select
                        value={selectedSessionId}
                        label={t(
                            "exportsComponents.ticketZTab.sessionSelector.label"
                        )}
                        onChange={(e) =>
                            setSelectedSessionId(String(e.target.value))
                        }
                    >
                        {physicalOptions.length > 0 && (
                            <ListSubheader>
                                {t(
                                    "exportsComponents.ticketZTab.sessionSelector.physicalGroup"
                                )}
                            </ListSubheader>
                        )}
                        {physicalOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                                {opt.label}
                            </MenuItem>
                        ))}
                        <ListSubheader>
                            {t(
                                "exportsComponents.ticketZTab.sessionSelector.externalGroup"
                            )}
                        </ListSubheader>
                        {externalSessions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ ml: "auto" }}>
                    <ExportButton
                        companyId={companyId}
                        reportType="ticket_z"
                        filters={filters}
                        extraFilters={extraFilters}
                    />
                </Box>
            </Paper>

            {loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : report ? (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {t("exportsComponents.ticketZTab.title", {
                            number: report.session?.ticketZNumber || "—",
                        })}
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    "exportsComponents.ticketZTab.sections.company"
                                )}
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {report.session?.companyName || "—"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    "exportsComponents.ticketZTab.sections.siret"
                                )}
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {report.session?.companySiret || "—"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    "exportsComponents.ticketZTab.sections.outlet"
                                )}
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {report.session?.outletName || "—"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    "exportsComponents.ticketZTab.sections.register"
                                )}
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {report.session?.registerName || "—"}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    "exportsComponents.ticketZTab.sections.status"
                                )}
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {report.session?.status?.toUpperCase()}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    "exportsComponents.ticketZTab.sections.opening"
                                )}
                            </Typography>
                            <Typography variant="body1">
                                {fmtDateTime(report.session?.openedAt)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    "exportsComponents.ticketZTab.sections.closing"
                                )}
                            </Typography>
                            <Typography variant="body1">
                                {fmtDateTime(report.session?.closedAt)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    "exportsComponents.ticketZTab.sections.openingFloat"
                                )}
                            </Typography>
                            <Typography variant="body1">
                                {fmtC(
                                    report.session?.openingFloatMinor || 0,
                                    report.session?.currency
                                )}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t(
                            "exportsComponents.ticketZTab.sections.salesSummary"
                        )}
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    "exportsComponents.ticketZTab.sections.salesCount"
                                )}
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {report.sales?.total_sales || 0}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    "exportsComponents.ticketZTab.sections.ttcRevenue"
                                )}
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {fmtC(
                                    report.sales?.total_revenue_minor || 0,
                                    report.sales?.currency
                                )}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t(
                            "exportsComponents.ticketZTab.sections.paymentMethodDetail"
                        )}
                    </Typography>
                    <TableContainer sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.paymentColumns.mode"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.paymentColumns.type"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.paymentColumns.ticketRef"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t(
                                            "exportsComponents.ticketZTab.paymentColumns.transactions"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.ht")}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.vat")}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.ttc")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(report.paymentMethods || []).map(
                                    (pm: any, i: number) => (
                                        <TableRow key={i}>
                                            <TableCell>{pm.label}</TableCell>
                                            <TableCell>
                                                {pm.type || "—"}
                                            </TableCell>
                                            <TableCell>
                                                {Array.isArray(
                                                    pm.ticket_refs
                                                ) && pm.ticket_refs.length > 0
                                                    ? pm.ticket_refs.join(", ")
                                                    : "—"}
                                            </TableCell>
                                            <TableCell align="right">
                                                {pm.transaction_count}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtC(
                                                    pm.net_total_minor || 0,
                                                    pm.currency
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtC(
                                                    pm.vat_total_minor || 0,
                                                    pm.currency
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtC(
                                                    pm.gross_total_minor || 0,
                                                    pm.currency
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                                {(!report.paymentMethods ||
                                    report.paymentMethods.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            {t(
                                                "exportsComponents.ticketZTab.empty.noPayments"
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t(
                            "exportsComponents.ticketZTab.sections.vatBreakdown"
                        )}
                    </Typography>
                    <TableContainer sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.vatColumns.rate"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.ht")}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.vat")}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.ttc")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(report.vatBreakdown || []).map(
                                    (vat: any, i: number) => (
                                        <TableRow key={i}>
                                            <TableCell>
                                                {vat.label} (
                                                {Number(vat.rate).toFixed(2)}%)
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtC(
                                                    vat.net_total_minor || 0,
                                                    vat.currency
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtC(
                                                    vat.vat_total_minor || 0,
                                                    vat.currency
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtC(
                                                    vat.gross_total_minor || 0,
                                                    vat.currency
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                                {(!report.vatBreakdown ||
                                    report.vatBreakdown.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            {t(
                                                "exportsComponents.ticketZTab.empty.noVatBreakdown"
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t(
                            "exportsComponents.ticketZTab.sections.cashMovementsDetail"
                        )}
                    </Typography>
                    <TableContainer sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.movementColumns.date"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.movementColumns.direction"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.movementColumns.reason"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.movementColumns.type"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.movementColumns.mode"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.movementColumns.note"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.ticketZTab.movementColumns.user"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t(
                                            "exportsComponents.ticketZTab.movementColumns.amount"
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(report.cashMovements || []).map(
                                    (movement: any) => (
                                        <TableRow
                                            key={
                                                movement.id ||
                                                `${movement.created_at}-${movement.reason_code}`
                                            }
                                        >
                                            <TableCell>
                                                {fmtDateTime(
                                                    movement.created_at
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {String(
                                                    String(
                                                        movement.direction || ""
                                                    ).toLowerCase() === "in"
                                                        ? t(
                                                              "exportsComponents.ticketZTab.direction.in"
                                                          )
                                                        : String(
                                                              movement.direction ||
                                                                  ""
                                                          ).toLowerCase() ===
                                                          "out"
                                                        ? t(
                                                              "exportsComponents.ticketZTab.direction.out"
                                                          )
                                                        : movement.direction ||
                                                          "—"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {movement.reason_code || "—"}
                                            </TableCell>
                                            <TableCell>
                                                {movement.movement_type || "—"}
                                            </TableCell>
                                            <TableCell>
                                                {movement.payment_method_type ||
                                                    "—"}
                                            </TableCell>
                                            <TableCell>
                                                {movement.note || "—"}
                                            </TableCell>
                                            <TableCell>
                                                {movement.created_by_user_display_name ||
                                                    "—"}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtC(
                                                    movement.amount_minor || 0,
                                                    movement.currency
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                                {(!report.cashMovements ||
                                    report.cashMovements.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            {t(
                                                "exportsComponents.ticketZTab.empty.noCashMovements"
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {report.session?.status === "closed" && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {t(
                                    "exportsComponents.ticketZTab.sections.cashCount"
                                )}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t(
                                            "exportsComponents.ticketZTab.sections.theoretical"
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight={500}
                                    >
                                        {fmtC(
                                            report.session?.expectedCashMinor ||
                                                0,
                                            report.session?.currency
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t(
                                            "exportsComponents.ticketZTab.sections.counted"
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight={500}
                                    >
                                        {fmtC(
                                            report.session?.countedCashMinor ||
                                                0,
                                            report.session?.currency
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t(
                                            "exportsComponents.ticketZTab.sections.difference"
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight={600}
                                        color={
                                            report.session?.discrepancyMinor ===
                                            0
                                                ? "success.main"
                                                : "error.main"
                                        }
                                    >
                                        {fmtC(
                                            report.session?.discrepancyMinor ||
                                                0,
                                            report.session?.currency
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Paper>
            ) : (
                <Paper sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                        {t("exportsComponents.ticketZTab.empty.selectSession")}
                    </Typography>
                </Paper>
            )}
        </Box>
    )
}

export default TicketZTab
